import React, { useState, useEffect } from 'react';
import { MessageSquare, X, Send, CheckCircle2, Mail, Phone } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { CONTACT_INFO } from '../mock/mock';

const FloatingContact = () => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', message: '' });

  // Close on Escape key
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const existing = JSON.parse(localStorage.getItem('rcd_enquiries') || '[]');
    existing.push({ ...form, source: 'floating', ts: new Date().toISOString() });
    localStorage.setItem('rcd_enquiries', JSON.stringify(existing));
    setSubmitted(true);
    toast({
      title: 'Message received',
      description: 'Thank you. Our team will reach out within one business day.'
    });
    setForm({ name: '', email: '', message: '' });
    setTimeout(() => {
      setSubmitted(false);
      setOpen(false);
    }, 2200);
  };

  return (
    <>
      {/* Floating trigger button */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={open ? 'Close contact form' : 'Open contact form'}
        aria-expanded={open}
        className="rcd-float-btn"
      >
        <span className="rcd-float-btn-inner">
          {open ? <X size={22} strokeWidth={2} /> : <MessageSquare size={22} strokeWidth={2} />}
        </span>
        {!open && <span className="rcd-float-btn-pulse" aria-hidden="true" />}
      </button>

      {/* Floating panel */}
      <div
        className={`rcd-float-panel ${open ? 'rcd-float-panel--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
      >
        <div className="rcd-float-panel-header">
          <div>
            <p className="text-[10px] tracking-[0.28em] uppercase text-[#d4b483] mb-1">Get in Touch</p>
            <h4 className="font-serif text-xl text-[#faf6f0] leading-tight">How can we help?</h4>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close"
            className="w-8 h-8 flex items-center justify-center text-[#c9bfae] hover:text-[#faf6f0] hover:bg-[#faf6f0]/10 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="rcd-float-panel-body">
          {submitted ? (
            <div className="flex flex-col items-center text-center py-8">
              <CheckCircle2 size={40} className="text-[#a67c3d] mb-4" />
              <h5 className="font-serif text-lg text-[#1a1614] mb-2">Message received</h5>
              <p className="text-sm text-[#4a3f36] max-w-xs">
                Thank you. Our atelier team will get back to you within one business day.
              </p>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-[10px] tracking-[0.2em] uppercase text-[#6b5847] mb-1.5">Your Name</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={form.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2.5 bg-transparent border border-[#e0d6c5] focus:border-[#1a1614] outline-none text-sm text-[#1a1614] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-[10px] tracking-[0.2em] uppercase text-[#6b5847] mb-1.5">Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    className="w-full px-3 py-2.5 bg-transparent border border-[#e0d6c5] focus:border-[#1a1614] outline-none text-sm text-[#1a1614] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-[10px] tracking-[0.2em] uppercase text-[#6b5847] mb-1.5">Message</label>
                  <textarea
                    name="message"
                    required
                    rows={3}
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us briefly about your project…"
                    className="w-full px-3 py-2.5 bg-transparent border border-[#e0d6c5] focus:border-[#1a1614] outline-none text-sm text-[#1a1614] transition-colors resize-none placeholder:text-[#a89a86]"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#1a1614] text-[#faf6f0] hover:bg-[#a67c3d] transition-colors text-sm tracking-wide"
                >
                  Send Message <Send size={14} />
                </button>
              </form>

              <div className="mt-5 pt-5 border-t border-[#e8dfd1] space-y-2.5">
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="flex items-center gap-2.5 text-xs text-[#4a3f36] hover:text-[#a67c3d] transition-colors"
                >
                  <Mail size={13} className="text-[#a67c3d]" />
                  {CONTACT_INFO.email}
                </a>
                <a
                  href={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`}
                  className="flex items-center gap-2.5 text-xs text-[#4a3f36] hover:text-[#a67c3d] transition-colors"
                >
                  <Phone size={13} className="text-[#a67c3d]" />
                  {CONTACT_INFO.phone}
                </a>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Backdrop overlay (mobile) */}
      {open && (
        <div
          className="rcd-float-backdrop"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default FloatingContact;
